import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import AuthLayout from '../components/AuthLayout';
import checklistTemplateService, { ChecklistTemplate } from '../services/checklistTemplate.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorFallback from '../components/ErrorFallback';
import EmptyState from '../components/EmptyState';

const serviceTypeLabels = {
  maintenance: 'Manutenção',
  installation: 'Instalação',
  inspection: 'Inspeção',
  repair: 'Reparo',
  other: 'Outro'
};

const ChecklistTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);

  useEffect(() => {
    loadChecklistTemplates();
  }, [serviceTypeFilter, activeFilter]);

  const loadChecklistTemplates = async () => {
    setLoading(true);
    setError(null);
    console.log('Buscando templates de checklist...');
    try {
      const filters = {
        search: search || undefined,
        serviceType: serviceTypeFilter || undefined,
        isActive: activeFilter
      };
      
      const response = await checklistTemplateService.getChecklistTemplates(filters);
      console.log('Resposta completa do serviço:', response);
      
      // O backend retorna { templates: [...] }
      const templatesList = response.templates || response.checklistTemplates || response || [];
      console.log('Templates de checklist recebidos:', templatesList);
      setChecklistTemplates(templatesList);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao carregar templates de checklist');
      console.error('Erro ao carregar templates de checklist:', error);
      // Definir uma lista vazia para evitar erros de renderização
      setChecklistTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadChecklistTemplates();
  };

  const handleDeleteClick = (id: string) => {
    setTemplateToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      await checklistTemplateService.deleteChecklistTemplate(templateToDelete);
      setChecklistTemplates(prevTemplates => 
        prevTemplates.map(template => 
          template._id === templateToDelete 
            ? { ...template, isActive: false } 
            : template
        )
      );
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao excluir template de checklist');
    } finally {
      setConfirmDeleteOpen(false);
      setTemplateToDelete(null);
    }
  };

  return (
    <AuthLayout title="Templates de Checklist">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Templates de Checklist
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/checklist-templates/new')}
          >
            Novo Template
          </Button>
        </Box>

        {/* Removido o alerta de erro, agora usamos o componente ErrorFallback */}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <form onSubmit={handleSearchSubmit} style={{ flex: 1 }}>
              <TextField
                fullWidth
                placeholder="Buscar templates..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button type="submit" variant="contained" size="small">
                        Buscar
                      </Button>
                    </InputAdornment>
                  )
                }}
              />
            </form>

            <TextField
              select
              label="Tipo de Serviço"
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="maintenance">Manutenção</MenuItem>
              <MenuItem value="installation">Instalação</MenuItem>
              <MenuItem value="inspection">Inspeção</MenuItem>
              <MenuItem value="repair">Reparo</MenuItem>
              <MenuItem value="other">Outro</MenuItem>
            </TextField>

            <TextField
              select
              label="Status"
              value={activeFilter === undefined ? '' : activeFilter ? 'active' : 'inactive'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') setActiveFilter(undefined);
                else setActiveFilter(value === 'active');
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="active">Ativos</MenuItem>
              <MenuItem value="inactive">Inativos</MenuItem>
            </TextField>
          </Box>
        </Paper>

        {loading ? (
          <LoadingIndicator message="Carregando templates de checklist..." />
        ) : error ? (
          <ErrorFallback 
            error={error} 
            resetErrorBoundary={loadChecklistTemplates} 
            message="Ocorreu um erro ao carregar os templates de checklist." 
          />
        ) : checklistTemplates.length === 0 ? (
          <EmptyState 
            title="Nenhum template de checklist encontrado" 
            message="Não foram encontrados templates de checklist com os filtros selecionados." 
            actionText="Criar Novo Template" 
            onAction={() => navigate('/checklist-templates/new')} 
          />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo de Serviço</TableCell>
                  <TableCell>Itens</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklistTemplates.map((template) => (
                  <TableRow key={template._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1">{template.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={serviceTypeLabels[template.serviceType] || template.serviceType} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{template.items.length} itens</TableCell>
                    <TableCell>
                      <Chip
                        label={template.isActive ? 'Ativo' : 'Inativo'}
                        color={template.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {template.createdAt
                        ? format(new Date(template.createdAt), 'dd/MM/yyyy', { locale: ptBR })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={() => navigate(`/checklist-templates/${template._id}`)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={template.isActive ? 'Desativar' : 'Já está inativo'}>
                          <span>
                            <IconButton
                              onClick={() => handleDeleteClick(template._id!)}
                              size="small"
                              disabled={!template.isActive}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este template de checklist? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default ChecklistTemplateList;
